import { useMemo } from 'react';
import styled from 'styled-components';
import { getDisplayCategoryForAsset } from '@frameio/graphql-client';
import type {
  Asset,
  VersionStackAsset,
} from '@frameio/graphql-client/generated';
import { CrustcrumbsView } from '@frameio/shared/components/Crustcrumbs';
import { isVersionInStack, isVersionStack } from '@frameio/shared/finder/utils';
import { EMPTY_OBJECT } from '@frameio/utils';
import { Box, MediaThumbnail, Text, theme } from '@frameio/vapor';
import { IconFolderClosed } from '@frameio/vapor-icons';
import type { QuickSearchAssetFragment } from '../../gql/generated/QuickSearchAsset';
import { useQuickSearchAssetFragment } from '../../gql/generated/QuickSearchAsset';
import { useNavigateToResult } from '../../hooks';
import { isFolder } from '../../utils';
import TEXT from './generated/TEXT/QuickSearch_ResultAssetItem';
import type { ResultItemProps } from './ResultItemCell';
import { ResultItemCell } from './ResultItemCell';

TEXT.DEFINE({
  /** Label for all assets breadcrumb */
  allAssets: 'All Assets',
});

const getMedia = (result: QuickSearchAssetFragment) => {
  if (
    result?.__typename === 'VideoAsset' ||
    result?.__typename === 'ModelAsset' ||
    result?.__typename === 'ImageAsset'
  ) {
    return {
      thumbs: {
        thumb: result?.thumbnailImageUrl?.url ?? '',
        thumb2: result?.thumbnailImageUrl?.url ?? '',
      },
    };
  }

  // Return an empty object if it does not have previewable media (thumbs):
  return EMPTY_OBJECT;
};

export const ResultAssetItem = (
  props: Omit<ResultItemProps, 'href'> & { resultId: string }
) => {
  const { resultId } = props;
  const { data: assetResultData } = useQuickSearchAssetFragment({
    from: { __typename: 'Asset', id: resultId },
  });

  const { id, __typename, name: fileName, project } = assetResultData;
  const { href } = useNavigateToResult(id ?? '', __typename ?? '');

  const isAssetVersionStack = isVersionStack(assetResultData as Asset);
  const isAssetVersion = isVersionInStack(assetResultData as Asset);
  let assetEntity = assetResultData;
  if (assetResultData && isAssetVersionStack) {
    // Use the top version asset for version stack
    assetEntity = (assetResultData as VersionStackAsset)
      .versions?.[0] as QuickSearchAssetFragment;
  }
  const assetType = assetEntity?.__typename;
  const parent = assetResultData.parent;
  const parentFolder = isAssetVersion
    ? (parent as VersionStackAsset)?.parent
    : parent;
  const isAtRootFolder =
    assetResultData.__typename === 'FolderAsset' &&
    parent?.id === assetResultData?.project?.rootAssetId;
  const parentFolderName = isAtRootFolder ? TEXT.allAssets : parentFolder?.name;
  const { displayCategory, media } = useMemo(() => {
    return {
      displayCategory: getDisplayCategoryForAsset(assetEntity),
      media: getMedia(assetEntity as QuickSearchAssetFragment),
    };
  }, [assetEntity]);
  const isItemFolder = isFolder(assetResultData?.__typename ?? '');

  const itemProps = {
    href,
    ...props,
  };

  return (
    <ResultItemCell {...itemProps}>
      <Box width="content">
        <Box
          alignItems="center"
          display="flex"
          height={24}
          justifyContent="center"
          width={24}
        >
          {isItemFolder ? (
            <IconFolderClosed color={theme.colors.icon.primary} size={16} />
          ) : (
            <MediaThumbnail
              assetTypename={assetType ?? ''}
              backgroundColor="transparent"
              displayCategory={displayCategory}
              height={24}
              isIconFallbackBackgroundVisible={false}
              isIconFallbackFullBleed={false}
              isImageFitPreferred={false}
              media={media}
              width={24}
            />
          )}
        </Box>
      </Box>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ContentGrid>
          <ContentRow>
            <Text
              truncate={{ hasExtension: true, padding: 0, type: 'middle' }}
              variant="body"
            >
              {fileName ?? ''}
            </Text>
          </ContentRow>
          {project?.name && (
            <ContentRow>
              <CrustcrumbsView
                folderName={parentFolderName}
                isAtRoot={isAtRootFolder}
                projectName={project.name}
              />
            </ContentRow>
          )}
        </ContentGrid>
      </Box>
      {/* @todo (ASSETS-1537): See version stack pill in results.
      {isAssetVersion && (
        <Column width="content">
          <VersionCount
            count={Math.abs(
              (quickSearchResult as VersionStackAsset)?.index
            )}
          />
        </Column>
      )}
      */}
    </ResultItemCell>
  );
};

const ContentGrid = styled.div`
  display: grid;
`;

const ContentRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: start;
  justify-items: start;
  overflow: hidden;
  white-space: nowrap;
`;
